import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {IRecordEmit} from '../../interfaces/record';
import {cloneDeep} from 'lodash';
import {ServiceAction, ServiceState} from '../../models/service';
import {ActivatedRoute, Params} from '@angular/router';
import {StorageService} from '../../services/storage/storage.service';
import {IUser} from 'src/app/models/auth/user.interface';
import {Loader} from '../../models/loader/loader.redux';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {AuthState} from 'src/app/models/auth/auth.redux';
import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {Store} from '@ngxs/store';
import {IRecordSleep} from 'src/app/models/record-sleep/record-sleep.interface';
import {NzMarks} from 'ng-zorro-antd/slider';
import {RecordSleepAction, SleepState} from '../../models/record-sleep';
import {SkeletonEnum} from '../../enums/skeleton';
import {SkeletonService} from '../../services/skeleton/skeleton.service';

@Component({
  selector: 'app-record-history-sleep',
  templateUrl: './record-history-sleep.component.html',
  styleUrls: ['./record-history-sleep.component.scss'],
  animations: [fadeInAnimation]
})
export class RecordHistorySleepComponent implements OnInit, OnDestroy {
  isPatient$: Observable<boolean> = this.store.select<boolean>(AuthState.getIsPatient);
  wasDeployed$: Observable<boolean> = this.store.select<boolean>(SleepState.getWasDeployed);
  @Input() recordSleep: IRecordSleep;
  @Output() sleepChange = new EventEmitter<IRecordEmit>();
  sleepRedux: IRecordSleep;

  form: UntypedFormGroup;
  serviceId: string;
  expertId: string;
  patientId: string;
  clinicId: string;

  userLogged: IUser;
  disableForm: boolean;
  loadInfo: boolean;

  wasDeployedSubscription: Subscription;

  marks: NzMarks = {
    0: '',
    1: '',
    2: '',
    3: '',
    4: ''
  };
  skeletonTemplate: any[] = [];
  constructor(
    private store: Store,
    private storageService: StorageService,
    private activatedRoute: ActivatedRoute,
    private utilitiesService: UtilitiesService,
    private messageAlert: MessageAlertService,
    private skeletonService: SkeletonService
  ) {
    this.disableForm = false;
    this.loadInfo = false;

    this.wasDeployedSubscription = this.wasDeployed$.subscribe(value => {
      if (value && !this.recordSleep) {
        this.loadInfo = false;
        this.initValuesHttp().then();
      }
    });
  }

  ngOnInit(): void {
    this.initValues();
  }

  initValues(): void {
    this.form = new UntypedFormGroup({});
    this.skeletonTemplate = this.skeletonService.build(SkeletonEnum.ROWS);
  }

  async initValuesHttp(): Promise<void> {
    try {
      // await firstValueFrom(this.store.dispatch(new Loader(true, 'Cargando índice de higiene del sueño del paciente ...')));
      this.getParams();
      this.userLogged = cloneDeep(this.storageService.getLocalItem<{ user: IUser, token: string }>('user')?.user);
      if (this.userLogged && this.userLogged.experts) {
        this.expertId = this.userLogged.experts[0]._id;
      } else if (this.userLogged && this.userLogged.patients) {
        const queryParams = this.activatedRoute.snapshot.queryParams;
        this.expertId = queryParams.expert;
      }
      this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      if (!this.serviceId) {
        await firstValueFrom(this.store.dispatch(new ServiceAction.GetActive(this.patientId, this.expertId)));
        this.serviceId = await firstValueFrom(this.store.selectOnce(ServiceState.getServiceSelectedId));
      }
      await firstValueFrom(this.store.dispatch(new RecordSleepAction.GetActive(this.serviceId, this.patientId)));
      this.sleepRedux = cloneDeep(await firstValueFrom(this.store.selectOnce(SleepState.getSleep)));
      this.recordSleep = cloneDeep(this.sleepRedux);
      this.sleepChange.emit({ record: this.recordSleep, updated: false });
      this.setFormProperties(this.sleepRedux);
      this.loadInfo = true;
    } catch (e) {
      console.error('[RecordHistorySleepComponent] ', e);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  setFormProperties(recordSleep: IRecordSleep): void {
    this.disableForm = !this.userLogged?.experts;
    this.form = new UntypedFormGroup({
      takeNapsTwoOrMoreHours: new UntypedFormControl(recordSleep ? recordSleep.takeNapsTwoOrMoreHours : 0, []),
      goToBedAtDifferentTimes: new UntypedFormControl(recordSleep ? recordSleep.goToBedAtDifferentTimes : 0, []),
      getUpBedAtDifferentTimes: new UntypedFormControl(recordSleep ? recordSleep.getUpBedAtDifferentTimes : 0, []),
      workUpSweatOneHourBeforeBed: new UntypedFormControl(recordSleep ? recordSleep.workUpSweatOneHourBeforeBed : 0, []),
      stayInBedMoreTwoOrThreeTimesAWeek: new UntypedFormControl(recordSleep ? recordSleep.stayInBedMoreTwoOrThreeTimesAWeek : 0, []),
      useAlcoholTobaccoOrCaffeineBeforeOrAfterBed: new UntypedFormControl(recordSleep ? recordSleep.useAlcoholTobaccoOrCaffeineBeforeOrAfterBed : 0, []),
      doSomethingBeforeBedThatMightWakeMeUp: new UntypedFormControl(recordSleep ? recordSleep.doSomethingBeforeBedThatMightWakeMeUp : 0, []),
      goToBedFeelingStressedAngryUpsetOrNervous: new UntypedFormControl(recordSleep ? recordSleep.goToBedFeelingStressedAngryUpsetOrNervous : 0, []),
      useBedForOtherThingsThanSleepingOrHavingSex: new UntypedFormControl(recordSleep ? recordSleep.useBedForOtherThingsThanSleepingOrHavingSex : 0, []),
      sleepOnAnUncomfortableBed: new UntypedFormControl(recordSleep ? recordSleep.sleepOnAnUncomfortableBed : 0, []),
      sleepInAnUncomfortableRoom: new UntypedFormControl(recordSleep ? recordSleep.sleepInAnUncomfortableRoom : 0, []),
      doSomeImportantWorkBeforeGoingToBed: new UntypedFormControl(recordSleep ? recordSleep.doSomeImportantWorkBeforeGoingToBed : 0, []),
      thinkPlanOrWorryWhenIAmInBed: new UntypedFormControl(recordSleep ? recordSleep.thinkPlanOrWorryWhenIAmInBed : 0, [])
    });
    if (!this.userLogged.experts) {
      this.form.disable();
    }
  }

  getParams(): void {
    const params: Params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.clinicId = params.clinicId;
    this.patientId = params.patientId;
  }

  emitChangeUpdate(): void {
    this.recordSleep.updatedAt = new Date().toISOString();
    this.sleepChange.emit({ record: this.recordSleep, updated: true });
  }

  sliderFormatter(value: number): string {
    let status = '';
    switch (value) {
      case 1:
        status = 'Nunca';
        break;
      case 2:
        status = 'Raramente';
        break;
      case 3:
        status = 'A veces';
        break;
      case 4:
        status = 'Frecuente';
        break;
      case 5:
        status = 'Siempre';
        break;
    }
    return status;
  }

  async onSave(): Promise<void> {
    this.form.markAllAsTouched();
    this.form.markAsDirty();

    try {
      await firstValueFrom(this.store.dispatch(new Loader(true, 'Guardando historial de sueño ...')));
      if (!this.sleepRedux?._id) {
        await firstValueFrom(this.store.dispatch(new RecordSleepAction.Post(this.serviceId, this.patientId, this.form.getRawValue())));
        const otherHabitsCreated: IRecordSleep = cloneDeep(
          await firstValueFrom(this.store.selectOnce(SleepState.getSleep)));
        if (!this.recordSleep) {
          this.recordSleep = cloneDeep(otherHabitsCreated);
        }
        this.sleepRedux._id = otherHabitsCreated?._id || null;
      } else {
        await firstValueFrom(this.store.dispatch(new RecordSleepAction.PatchActive(
          this.serviceId, this.patientId, this.sleepRedux._id, this.form.getRawValue())
        ));
      }
      this.messageAlert.createMessage('success', 'Historial de sueño guardado exitosamente.');
      this.emitChangeUpdate();
    } catch (e) {
      console.error('[onSave] ', e);
      this.messageAlert.createMessage('error', 'Ha ocurrido un error al actualizar el historial de sueño. Intente más tarde', 5000);
    } finally {
      await firstValueFrom(this.store.dispatch(new Loader(false)));
    }
  }

  ngOnDestroy(): void {
    this.wasDeployedSubscription.unsubscribe();
  }
}
